import { Component, OnInit, HostListener } from '@angular/core';
import { APP_CONFIG } from '../../../shared/constants';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

@Component({
  selector: 'app-install-prompt',
  templateUrl: './install-prompt.component.html',
  styleUrls: ['./install-prompt.component.scss']
})
export class InstallPromptComponent implements OnInit {
  showInstallPrompt = false;
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private readonly INSTALL_DISMISSED_KEY = 'pwa-install-dismissed';
  private readonly DISMISS_DURATION = APP_CONFIG.INSTALL_DISMISS_DURATION_MS;

  ngOnInit() {
    const dismissed = localStorage.getItem(this.INSTALL_DISMISSED_KEY);
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      if (Date.now() - dismissedTime < this.DISMISS_DURATION) {
        return;
      }
    }

    if (this.isAppInstalled()) {
      return;
    }

    setTimeout(() => {
      if (this.deferredPrompt) {
        this.showInstallPrompt = true;
      }
    }, APP_CONFIG.INSTALL_PROMPT_DELAY_MS);
  }

  @HostListener('window:beforeinstallprompt', ['$event'])
  onBeforeInstallPrompt(event: Event) {
    event.preventDefault();
    this.deferredPrompt = event as BeforeInstallPromptEvent;
  }

  @HostListener('window:appinstalled')
  onAppInstalled(): void {
    this.showInstallPrompt = false;
    this.deferredPrompt = null;
  }

  async installApp() {
    if (!this.deferredPrompt) {
      return;
    }

    await this.deferredPrompt.prompt();
    const { outcome } = await this.deferredPrompt.userChoice;

    this.deferredPrompt = null;
    this.showInstallPrompt = false;

    if (outcome === 'dismissed') {
      localStorage.setItem(this.INSTALL_DISMISSED_KEY, Date.now().toString());
    }
  }

  closePrompt() {
    this.showInstallPrompt = false;
    localStorage.setItem(this.INSTALL_DISMISSED_KEY, Date.now().toString());
  }

  private isAppInstalled(): boolean {
    // standalone = PWA instalado; fullscreen = TWA
    return window.matchMedia('(display-mode: standalone)').matches ||
           window.matchMedia('(display-mode: fullscreen)').matches ||
           (window.navigator as { standalone?: boolean }).standalone === true;
  }
}
